import React from 'react';
import { KINDS, KIND_LABEL } from './vendorUi';
import { ringOn, INK, INSET, MUTED, SUBTLE, CHIP, RULE, PillButton } from '../../club/clubUi';
import { AMBER_BG, AMBER_TEXT } from '../setup/setupUi';
import { visibleFor, stepLabel } from '../setup/proSetupSteps';
import useMediaQuery from '../../../utils/useMediaQuery';

/**
 * KIND (design_handoff_pro_setup README "KIND"): five cards, single select; the selected one is
 * black with the rainbow ring. Under the cards, the sections that kind gives the page.
 *
 * Stepper: a tap applies at once (`onChange`). EDIT PAGE: a tap proposes (`onPropose`) and an
 * amber confirm card asks SWITCH / KEEP, because the list starts over and nothing changes on the
 * page until SAVE. `pending` is the proposed kind, `onConfirm` / `onCancel` close it.
 */
export default function KindPicker({ value, onChange, mode = 'create', pending = null, onPropose, onConfirm, onCancel }) {
  const phone = useMediaQuery('(max-width: 639px)');
  const wide = useMediaQuery('(min-width: 1000px)');
  const shown = pending && pending !== value ? pending : value;
  const sections = visibleFor(shown).filter((k) => k !== 'kind').map((k) => stepLabel(k, shown, 'edit'));
  const pick = (key) => {
    if (key === value) { onCancel?.(); return; }
    if (mode === 'edit' && onPropose) onPropose(key); else onChange(key);
  };
  return (
    <div className="flex flex-col font-mono" style={{ gap: 18 }}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <h2 className="m-0 font-mono" style={{ fontSize: phone ? 18 : 22, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase' }}>WHAT KIND OF BUSINESS IS THIS?</h2>
        <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6, color: '#374151' }}>This sets what your page asks you for and what people see. You can change it later.</p>
      </div>
      <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : wide ? 'repeat(3,minmax(0,1fr))' : 'repeat(2,minmax(0,1fr))', gap: 10 }} role="radiogroup" aria-label="Kind of business">
        {KINDS.map((k) => {
          const on = value === k.key;
          const pend = pending && pending !== value && pending === k.key;
          return (
            <button key={k.key} type="button" role="radio" aria-checked={on} onClick={() => pick(k.key)} data-testid={`kind-${k.key}`}
              className="flex flex-col justify-between text-left font-mono transition-colors duration-200"
              style={{ gap: 18, minHeight: phone ? 0 : 200, padding: 20, border: '2px solid transparent', borderRadius: 22, background: on ? ringOn : INSET, color: on ? '#fff' : INK, boxShadow: pend ? 'inset 0 0 0 3px #000' : 'none', cursor: 'pointer' }}>
              <span className="flex flex-col" style={{ gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>{k.label}</span>
                <span style={{ fontSize: 12, lineHeight: 1.5, color: on ? SUBTLE : MUTED }}>{k.blurb}</span>
              </span>
              <span className="flex flex-wrap" style={{ gap: 5 }}>
                <span className="w-full" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: on ? SUBTLE : MUTED }}>UNLOCKS</span>
                {k.blocks.map((b) => (
                  <span key={b} style={{ padding: '5px 9px', borderRadius: 999, background: on ? '#262626' : '#fff', color: on ? '#fff' : INK, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{b}</span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
      {pending && pending !== value && (
        <div className="flex flex-wrap items-center" style={{ gap: 12, padding: '16px 18px', borderRadius: 20, background: AMBER_BG }} data-testid="kind-pending">
          <span className="flex flex-col" style={{ flex: '1 1 280px', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>SWITCH TO {KIND_LABEL[pending]}?</span>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: AMBER_TEXT }}>Your {stepLabel('list', value, 'edit').toLowerCase()} list starts over. Photos, hours and where stay. Nothing changes on your page until you save.</span>
          </span>
          <span className="flex" style={{ gap: 6 }}>
            <PillButton size={42} onClick={onConfirm} data-testid="kind-switch">SWITCH</PillButton>
            <PillButton variant="ghost" size={42} onClick={onCancel} data-testid="kind-keep">KEEP</PillButton>
          </span>
        </div>
      )}
      <div className="flex flex-col" style={{ gap: 8, paddingTop: 14, borderTop: RULE }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: MUTED }}>{pending && pending !== value ? 'YOUR PAGE WOULD HAVE' : 'YOUR PAGE HAS THESE SECTIONS'}</span>
        <span className="flex flex-wrap" style={{ gap: 6 }} data-testid="kind-sections">
          {sections.map((l) => <span key={l} style={{ padding: '6px 12px', borderRadius: 999, background: CHIP, fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>{l}</span>)}
        </span>
      </div>
    </div>
  );
}
